import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import apiClient, { API, getErrorMessage } from "@/lib/apiClient";
import { Download, FileText, Search, Signature } from "lucide-react";

export default function WaiverSignatures() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/waiver/signatures", { params: email ? { email } : {} });
      setRows(r.data || []);
    } catch (e) { toast.error(getErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openDetail = async (id) => {
    setSelected(id); setDetail(null);
    try { const r = await apiClient.get(`/admin/waiver/signatures/${id}`); setDetail(r.data); }
    catch (e) { toast.error(getErrorMessage(e, "Load failed")); }
  };

  const token = () => localStorage.getItem("token");

  const downloadCSV = () => {
    const url = `${API}/admin/waiver/signatures/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "waiver-signatures.csv";
        a.click();
      });
  };

  const downloadPDF = (id) => {
    const url = `${API}/admin/waiver/signatures/${id}/export.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `waiver-${id.slice(0, 8)}.pdf`;
        a.click();
      });
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-4 max-w-6xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Signature className="w-8 h-8 text-pink-400" /> Waiver signatures</h1>
          <Button onClick={downloadCSV} className="bg-white/10 hover:bg-white/15" data-testid="waiver-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        <Card className="p-4 bg-white/5 border-white/10 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Filter by email…" value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-black/30 border-white/10 text-white" data-testid="waiver-search-input" />
          </div>
          <Button onClick={load} className="bg-white/10 hover:bg-white/15" data-testid="waiver-search-btn">
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
        </Card>

        <Card className="p-0 bg-white/5 border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-400 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-slate-400 text-center">No signatures yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/30 text-slate-400 uppercase text-xs">
                  <tr>
                    <th className="text-left p-3">Signed at</th>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Version</th>
                    <th className="text-left p-3">IP</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`waiver-sig-row-${r.id}`}>
                      <td className="p-3">{new Date(r.signed_at).toLocaleString()}</td>
                      <td className="p-3">{r.full_name}</td>
                      <td className="p-3">{r.email}</td>
                      <td className="p-3">v{r.version_number}</td>
                      <td className="p-3 text-slate-500 text-xs">{r.ip_address}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => openDetail(r.id)} className="chip mr-1" data-testid={`waiver-view-${r.id}`}>View</button>
                        <button onClick={() => downloadPDF(r.id)} className="chip" data-testid={`waiver-pdf-${r.id}`}>
                          <FileText className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {selected && (
          <div onClick={() => setSelected(null)} className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
              <h2 className="text-white text-xl font-bold mb-2">{detail?.full_name || "…"}</h2>
              {detail && (
                <>
                  <div className="text-slate-400 text-sm space-y-1 mb-4">
                    <div><span className="text-slate-500">Email:</span> {detail.email}</div>
                    <div><span className="text-slate-500">Signed at:</span> {new Date(detail.signed_at).toLocaleString()}</div>
                    <div><span className="text-slate-500">Version:</span> v{detail.version_number}</div>
                    <div><span className="text-slate-500">IP:</span> {detail.ip_address}</div>
                    {detail.phone && <div><span className="text-slate-500">Phone:</span> {detail.phone}</div>}
                    {detail.date_of_birth && <div><span className="text-slate-500">DOB:</span> {detail.date_of_birth}</div>}
                  </div>
                  <div className="bg-white p-4 rounded-xl mb-4">
                    <img src={`data:image/png;base64,${detail.signature_png}`} alt="signature" className="max-h-40 mx-auto" />
                  </div>
                  <Button onClick={() => downloadPDF(detail.id)} className="w-full" data-testid="waiver-detail-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
